import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import axios, { AxiosError } from "axios";
import { useLocation } from "react-router-dom";

import { nuclearn_api_url } from "src/App";
import {
  HelpSurvey,
  isHelpFeatureEnabled,
  HelpFlag,
  submitHelpSurvey,
} from "src/config/posthog";

const SUPPORT_EMAIL = "support@nuclearn.ai";

const TICKET_TYPES = ["Bug", "Feature Request", "Question", "Feedback"];

// Mirrors the backend `description` Pydantic constraint
// (`max_length=5000`). Out-of-sync values would let the form submit a
// payload the backend rejects with `422 string_too_long`.
const MAX_DESCRIPTION_LENGTH = 5000;

interface PydanticValidationError {
  type?: string;
  loc?: (string | number)[];
}

const isDescriptionTooLongError = (data: unknown): boolean => {
  const detail = (data as { detail?: unknown } | undefined)?.detail;
  if (!Array.isArray(detail)) return false;
  return detail.some(
    (item: PydanticValidationError) =>
      item?.type === "string_too_long" &&
      Array.isArray(item.loc) &&
      item.loc[item.loc.length - 1] === "description",
  );
};

interface ContactSupportFormProps {
  product: string;
}

const ContactSupportForm = ({ product }: ContactSupportFormProps) => {
  const location = useLocation();
  const flagEnabled = isHelpFeatureEnabled(HelpFlag.CONTACT_SUPPORT);

  const [type, setType] = useState("Question");
  const [description, setDescription] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  if (!flagEnabled) {
    return (
      <Alert severity="info" variant="outlined">
        Can&apos;t find what you need? Email{" "}
        <Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link> — our team
        will follow up.
      </Alert>
    );
  }

  if (submitted) {
    return (
      <Alert
        severity="success"
        variant="outlined"
        data-testid="help-support-success"
      >
        Message sent — our team will follow up.
      </Alert>
    );
  }

  const handleSubmit = async () => {
    setError(null);
    setSending(true);
    // Compliance: only typed description + generic metadata cross the boundary.
    // No conversation history, document content, or AI summaries.
    const payload = {
      type,
      description,
      product,
      route: location.pathname,
    };
    try {
      await axios.post(`${nuclearn_api_url}/support/contact`, payload);
      // Fire-and-forget analytics; failure here must not block the user.
      submitHelpSurvey(HelpSurvey.CONTACT_SUPPORT, payload);
      setSubmitted(true);
    } catch (err) {
      if (
        err instanceof AxiosError &&
        err.response?.status === 422 &&
        isDescriptionTooLongError(err.response.data)
      ) {
        setError(
          `Description must be ${MAX_DESCRIPTION_LENGTH} characters or fewer.`,
        );
      } else {
        const raw =
          err instanceof AxiosError ? err.response?.data?.detail : undefined;
        const detail = typeof raw === "string" ? raw : undefined;
        setError(
          detail ||
            `Couldn't submit through the app. Please email ${SUPPORT_EMAIL} directly.`,
        );
      }
    } finally {
      setSending(false);
    }
  };

  const descriptionLength = description.length;
  const overLimit = descriptionLength > MAX_DESCRIPTION_LENGTH;
  const canSubmit = description.trim().length > 0 && !overLimit && !sending;

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        Contact Support
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Can&apos;t find what you need? Send us a message.
      </Typography>

      <TextField
        select
        label="Type"
        value={type}
        onChange={(e) => setType(e.target.value)}
        size="small"
        data-testid="help-support-type"
      >
        {TICKET_TYPES.map((t) => (
          <MenuItem key={t} value={t}>
            {t}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        label="Tell us what's going on"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        multiline
        minRows={4}
        size="small"
        inputProps={{
          maxLength: MAX_DESCRIPTION_LENGTH,
          "data-testid": "help-support-message",
        }}
        error={overLimit}
        helperText={`${descriptionLength}/${MAX_DESCRIPTION_LENGTH}`}
        FormHelperTextProps={{ sx: { textAlign: "right" } }}
      />

      {error && <Alert severity="warning">{error}</Alert>}

      <Box>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          data-testid="help-support-send"
        >
          {sending ? "Sending…" : "Send"}
        </Button>
      </Box>
    </Stack>
  );
};

export default ContactSupportForm;
